<script setup lang="ts">
import { ReactiveConversation } from "~/classes/conversation/ReactiveConversation"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { ReactiveFile } from "~/classes/file/ReactiveFile"
import { ReactiveSearch } from "~/classes/search/ReactiveSearch"

const { state: sidebarState, sidebarFavoriteFiles, sidebarFavoriteConversations, sidebarFavoriteSearches } = useSidebar()
const { state: gestureState } = useGestures()
const { sidebarFavoritesId } = useGlobals()

const { unwatch: unwatchFiles, reactive: reactiveFiles } = ReactiveFile.toReactiveMap(
  sidebarFavoriteFiles,
  (file) => {
    return new ReactiveFile({
      file: shallowRef(file),
      selection: InterfaceSelection.minimumSelfSidebarSelection(),
      pathSelection: InterfaceSelection.minimumPathSelection(),
      location: "sidebar",
      options: { immediate: true, noLifecycle: true },
    })
  },
)

const { unwatch: unwatchConversations, reactive: reactiveConversations } = ReactiveConversation.toReactiveMap(
  sidebarFavoriteConversations,
  (conversation) => {
    return new ReactiveConversation({
      conversation: shallowRef(conversation),
      selection: InterfaceSelection.minimumConversationViewSelection(),
      location: "sidebar",
      options: { immediate: true, noLifecycle: true },
    })
  },
)

const { unwatch: unwatchSearches, reactive: reactiveSearches } = ReactiveSearch.toReactiveMap(
  sidebarFavoriteSearches,
  (search) => {
    return new ReactiveSearch({
      search: shallowRef(search),
      selection: InterfaceSelection.minimumSearchInterfaceViewSelection(),
      location: "sidebar",
      options: { immediate: true, noLifecycle: true },
    })
  },
)

// We created a computed value that is a sorted list of the reactive files, which is what
// we render to the UI
const sortedSidebarItems = computed(() => {
  const files = Object
    .entries(sidebarState.value.favoriteFiles)
    .filter(([_, file]) => isDefined(reactiveFiles.value[file.id]))
    .map(([fractionalIndex, file]) => ({
      fractionalIndex,
      file: reactiveFiles.value[file.id],
    }))
  const conversations = Object
    .entries(sidebarState.value.favoriteConversations)
    .filter(([_, conversation]) => isDefined(reactiveConversations.value[conversation.id]))
    .map(([fractionalIndex, conversation]) => ({
      fractionalIndex,
      conversation: reactiveConversations.value[conversation.id],
    }))
  const searches = Object
    .entries(sidebarState.value.favoriteSearches)
    .filter(([_, search]) => isDefined(reactiveSearches.value[search.id]))
    .map(([fractionalIndex, search]) => ({
      fractionalIndex,
      search: reactiveSearches.value[search.id],
    }))

  // sort all the items by fractional index
  const all = [...files, ...conversations, ...searches]
  return all.sort((a, b) => {
    return a.fractionalIndex.localeCompare(b.fractionalIndex)
  })
})

function showInsertBefore(fractionalIndex: string) {
  if (!isDefined(gestureState.value.sidebarFavoritesDraggedBetween))
    return false
  return fractionalIndex === gestureState.value.sidebarFavoritesDraggedBetween[1]
}

function showInsertAfter() {
  if (!isDefined(gestureState.value.sidebarFavoritesDraggedBetween))
    return false
  if (isDefined(gestureState.value.sidebarFavoritesDraggedBetween[1]))
    return false
  return true
}

onUnmounted(() => {
  unwatchFiles()
  unwatchConversations()
  unwatchSearches()
})
</script>

<template>
  <sidebar-section :id="sidebarFavoritesId">
    <template #label>
      Favorites
    </template>
    <template #default>
      <div class="files">
        <template
          v-for="item in sortedSidebarItems"
          :key="item.fractionalIndex"
        >
          <div v-if="showInsertBefore(item.fractionalIndex)" class="drop-placeholder" />
          <sidebar-entry-file
            v-if="'file' in item && !item.file.isBlank() && item.file.isFolder()"
            :fractional-index="item.fractionalIndex"
            :file="item.file"
            :hoverable="gestureState.gesture !== 'drag-file'"
          />
          <sidebar-entry-file
            v-else-if="'file' in item"
            :fractional-index="item.fractionalIndex"
            :file="item.file"
            :hoverable="gestureState.gesture !== 'drag-file'"
          />
          <sidebar-entry-search
            v-else-if="'search' in item"
            :fractional-index="item.fractionalIndex"
            :search="item.search"
            :hoverable="gestureState.gesture !== 'drag-file'"
            :drag-over="gestureState.sidebarItemDraggedOver?.search?.id === item.search.id"
          />
          <sidebar-entry-conversation
            v-else-if="'conversation' in item"
            :fractional-index="item.fractionalIndex"
            :conversation="item.conversation.asStatic()"
            :hoverable="gestureState.gesture !== 'drag-file'"
            :drag-over="gestureState.sidebarItemDraggedOver?.conversation?.id === item.conversation.id"
          />
        </template>
        <div v-if="showInsertAfter()" class="drop-placeholder" />
      </div>
    </template>
  </sidebar-section>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.files
  @apply flex flex-col gap-[2px] // FIXME: boltzmann variables

.drop-placeholder
  @apply h-[2px] bg-[#{$menu-b-separator}] rounded-[#{$sidebar-br-item-hovered}] my-[2px] -mx-[6px]
</style>
